import React from "react";
import { Box, Stack, Typography, CircularProgress } from "@mui/material";
import { VideoComment } from "protogen/video_pb";
import { getVideoComments } from "services/video";
import DocumentViewer from "components/editor/DocumentViewer";
import DateDisplay from "components/common/DateDisplay";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import { VideoCallContext } from "components/context/VideoCallContext";

interface VideoCommentsListPanelProps {
  videoRoomUuid: string;
  token?: string;
}

const VideoCommentEntry: React.FC<{
  entry: VideoComment;
}> = ({ entry }) => {
  const time = new Date(Number(entry.timestampSec) * 1000);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          mb: 0.5,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {entry.participantName}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          <DateDisplay date={time} />
        </Typography>
      </Box>
      <Box>
        {entry.body?.payload ? (
          <DocumentViewer
            content={JSON.parse(entry.body.payload)}
            attachments={entry.body.attachments}
            minHeight="20px"
          />
        ) : (
          <Typography variant="body2">
            {entry.body?.textContent || ""}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const VideoCommentsListPanel: React.FC<VideoCommentsListPanelProps> = ({
  videoRoomUuid,
  token: propToken,
}) => {
  const [comments, setComments] = React.useState<VideoComment[]>([]);
  const [loading, setLoading] = React.useState(true);
  const messagesRef = React.useRef<HTMLDivElement>(null);
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { videoCallState } = React.useContext(VideoCallContext);

  const effectiveToken = propToken || videoCallState.token;

  React.useEffect(() => {
    const loadComments = async () => {
      if (!effectiveToken || !videoRoomUuid) {
        setLoading(false);
        return;
      }
      const response = await getVideoComments(
        { videoRoomUuid },
        effectiveToken,
      );
      setComments(response.comments);
      setLoading(false);
    };

    loadComments();
  }, [videoRoomUuid, effectiveToken]);

  React.useEffect(() => {
    if (!videoRoomUuid) return;

    const handleNewComment = (channel: string, data: any) => {
      const newComment = data.message_payload as VideoComment;
      setComments((prev) => [...prev, newComment]);
    };

    const channelPrefix = `video-comment-${videoRoomUuid}`;
    registerHandler(channelPrefix, handleNewComment);

    return () => {
      unregisterHandler(channelPrefix, handleNewComment);
    };
  }, [videoRoomUuid, registerHandler, unregisterHandler]);

  React.useEffect(() => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [comments]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (comments.length === 0) {
    return (
      <Stack
        spacing={1}
        sx={{
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "text.secondary",
          px: 4,
        }}
      >
        <Typography variant="body2">No comments yet.</Typography>
        <Typography variant="caption">
          Comments from the video call will appear here.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      ref={messagesRef}
      sx={{
        height: "100%",
        overflowY: "auto",
      }}
    >
      {comments.map((entry) => (
        <VideoCommentEntry key={entry.ref} entry={entry} />
      ))}
    </Box>
  );
};

export default VideoCommentsListPanel;
